import { inventory as inventoryApi } from "./inventory";

const apiUrl = process.env.REACT_APP_API_ENDPOINT;

export const showLastEvents = async (props, state) => {
  const { facilityId = "", organizationId, token } = props;

  const { isExport = false, page = 0 } = state;

  let elasticQuery = {
    elasticSearchQuery: {
      bool: {
        must: [
          {
            term: {
              current_owner_id: organizationId,
            },
          },
          {
            bool: {
              must_not: [
                {
                  nested: {
                    path: "device",
                    query: {
                      exists: {
                        field: "device.status",
                      },
                    },
                  },
                },
              ],
            },
          },
        ],
        must_not: [
          {
            terms: {
              // If the user is on the Inventory Snapshot page, then we do not need
              // to exclude this
              asset_mode: [
                "Device",
                "Inventory",
                "Product",
              ],
            },
          },
          {
            term: {
              event: "External Scanned"
            }
          },
          {
            term: {
              event: "Proximity"
            }
          },
          {
            term: {
              event: "Moving Proximity"
            }
          },
          {
            regexp: {
              "last_event": {
                "value": "Scan.*LogisticsPro",
              }
            }
          },
        ],
        should: [],
      },
    },
    limit: "",
    range: {},
    page,
    sort: 'time_of_log DESC, tag DESC'
  };

  elasticQuery.limit = isExport ? 1000 : 10000;

  //if facilityId is not empty, add it to the query
  if (facilityId) {
    elasticQuery.elasticSearchQuery.bool.must.push({
      term: {
        facility_id: facilityId,
      },
    });
  }

  const results = await fetch(`${apiUrl}assets/search?includeConsoleExtras=true`, {
    method: "POST",
    headers: {
      "auth-token": token,
      "Content-Type": "application/json",
    },
    // body: JSON.stringify(elasticQuery),
    body: JSON.stringify(elasticQuery),
  })
    .then((response) => response.json())
    .then((json) => {
      // Map latitude/longitude - prioritize event-specific lat/long from propertiesMap
      // over root level (which may be facility coordinates)
      if (json.assets && json.assets.length > 0) {
        json.assets = json.assets.map((asset) => {
          // Priority: propertiesMap (event GPS) > root level
          const latitude = asset.propertiesMap?.latitude || 
            asset.latitude || 
            null;
          const longitude = asset.propertiesMap?.longitude || 
            asset.longitude || 
            null;
          
          return {
            ...asset,
            latitude: latitude ? parseFloat(latitude) : null,
            longitude: longitude ? parseFloat(longitude) : null,
          };
        });
      }
      return json;
    })
    .catch((err) => {
      console.log(err);
      console.log(elasticQuery);
      return {
        error: "Failed to fetch data, please contact system administrator.",
      };
    });
  return results;
};

export const saveFilterSettings = async (props, filters) => {
  const { token, appUserId } = props;
  const payload = {
    propertiesMap: {
      assetStatusTableSettings: filters,
    },
  };
  const results = await fetch(`${apiUrl}appUsers/${appUserId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "auth-token": token,
    },
    body: JSON.stringify(payload),
  })
    .then((results) => results.json())
    .then((results) => results)
    .catch((err) => {
      console.log(err);
      console.log(payload);
      return {
        error: "Failed to save settings, please contact system administrator.",
      };
    });

  return results;
};

export const retrieveParentIds = async (props) => {
  const { token } = props;
  const results = await fetch(`${apiUrl}assets/assetParents?includeConsoleExtras=true`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "auth-token": token,
    },
  })
    .then((results) => results.json())
    .then((results) => {
      return results;
    });

  return results;
};

const key = (v) => (v != null ? String(v).trim() : "");

/** Match kit child (often LX Tag parent) to Inventory Status row. */
const findInventoryForChild = (child, inventoryAssets, parentAssetIdTagMap = {}) => {
  const childId = key(child.assetId);
  const childTag = key(child.tag ?? child.assetTag);

  const self = inventoryAssets.find((inv) => key(inv.assetId) === childId);
  if (self) return self;

  if (childId) {
    const byParentId = inventoryAssets.find(
      (inv) => key(inv.parentId ?? inv.parent_id) === childId
    );
    if (byParentId) return byParentId;
  }

  if (childTag) {
    const byParentTag = inventoryAssets.find((inv) => {
      const pid = key(inv.parentId ?? inv.parent_id);
      return pid && key(parentAssetIdTagMap[pid]) === childTag;
    });
    if (byParentTag) return byParentTag;
  }

  return null;
};

/**
 * Fetch kit children by parent_id, then map Inventory Status fields
 * (tag, category, description, quantityCurrent) onto each row.
 */
export const searchChildrenByParentId = async (props, parentAssetId) => {
  const { organizationId, token } = props;
  const elasticQuery = {
    elasticSearchQuery: {
      bool: {
        must_not: [
          {
            regexp: {
              event: { value: "Scan.*LogisticsPro" },
            },
          },
        ],
        must: [
          { term: { current_owner_id: organizationId } },
          { term: { parent_id: parentAssetId } },
        ],
      },
    },
    limit: 1000,
    sort: "time_of_log DESC",
  };

  const json = await fetch(`${apiUrl}assets/search?includeConsoleExtras=true`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "auth-token": token,
    },
    body: JSON.stringify(elasticQuery),
  })
    .then((res) => res.json())
    .catch(() => ({ assets: [], error: true }));

  const children = (json.assets || []).map((asset) => {
    const latitude = asset.propertiesMap?.latitude ?? asset.latitude ?? null;
    const longitude = asset.propertiesMap?.longitude ?? asset.longitude ?? null;
    return {
      ...asset,
      latitude: latitude != null ? parseFloat(latitude) : null,
      longitude: longitude != null ? parseFloat(longitude) : null,
    };
  });

  if (!children.length) return children;

  try {
    const invJson = await inventoryApi.showLastEvents(
      { organizationId, token },
      { page: 0 }
    );
    const inventoryAssets = invJson?.assets || [];
    const parentAssetIdTagMap = invJson?.parentAssetIdTagMap || {};

    return children.map((child) => {
      const inv = findInventoryForChild(
        child,
        inventoryAssets,
        parentAssetIdTagMap
      );
      if (!inv) return child;

      const qty = Array.isArray(inv.quantity) ? inv.quantity[0] : inv.quantity;
      return {
        ...child,
        inventoryAssetId: inv.tag ?? null,
        category: inv.category ?? child.category ?? null,
        propertiesMap: {
          ...(child.propertiesMap || {}),
          description:
            inv.propertiesMap?.description ??
            child.propertiesMap?.description ??
            null,
        },
        quantity: qty || child.quantity,
      };
    });
  } catch (err) {
    console.warn("Kit children inventory mapping skipped", err);
    return children;
  }
};

export const assets = {
  retrieveParentIds,
  saveFilterSettings,
  searchChildrenByParentId,
  showLastEvents,
};
